import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import type { CostPredictionResponse, Nurse, NurseData } from "../../utils/models";
import type { ShiftAvailabilityWindow } from "../../utils/shiftAvailability";
import { getDayWeekScheduleIndex } from "../../utils/utilFunctions";
import { DIFF_HOURS_WARNING } from "../../utils/warningDialogs/utils";
import { buildCandidateRow, type CandidateRowContext } from "./addStaffRows";

const DAYS_IN_STRIP = 7;

type Args = {
	candidates: readonly Nurse[];
	/** `null` while no shift is picked — there are no rows to build until then. */
	window: ShiftAvailabilityWindow | null;
	weekIndex: number;
	dayNo: number;
	shiftNo: number;
	staffingDate: DateTime;
	predictions: Record<number, CostPredictionResponse>;
	nurseOccurrences: Record<number, number>;
	showActual: boolean;
};

/** The seven days the row's week strip shows, and which rotation week the staffing date falls in. */
const useWeekStrip = (staffingDate: DateTime) => {
	const { chosenDepartment } = useHospitalData();
	const dateRange = useMemo<DateTime[]>(() => {
		const startOfWeek = staffingDate.startOf("week");
		return Array.from({ length: DAYS_IN_STRIP }, (_, i) => startOfWeek.plus({ day: i }));
	}, [staffingDate]);
	const weekScheduleIndex = useMemo(() => getDayWeekScheduleIndex(chosenDepartment, staffingDate), [chosenDepartment, staffingDate]);
	return { dateRange, weekScheduleIndex };
};

/**
 * The step-2 grid rows: every candidate with their week, predicted cost, and whether they can work the
 * picked window (`resolveShiftAvailability` — blocked rows carry the reason, available rows the soft
 * badges). Rebuilds when the window, the candidates or the predictions change.
 */
export const useAddStaffRows = ({ candidates, window, weekIndex, dayNo, shiftNo, staffingDate, predictions, nurseOccurrences, showActual }: Args) => {
	const { t } = useTranslation();
	const { hospitalData, chosenDepartment, getShiftTimes } = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const { dateRange, weekScheduleIndex } = useWeekStrip(staffingDate);

	const rows = useMemo<NurseData[]>(() => {
		if (!window) return [];
		const context: CandidateRowContext = {
			window,
			weeks: hospitalData.weeks,
			weekIndex,
			dayNo,
			shiftNo,
			staffingDate,
			dateRange,
			weeklyShifts: hospitalData.weeks[weekIndex].weeklyShifts,
			departmentId: chosenDepartment.department_id,
			dayStart: chosenDepartment.dayStart,
			weekScheduleIndex,
			// DEFAULT: a department with no break rules deducts no breaks — same as the old picker's `|| []`.
			breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
			// DEFAULT: the same long-day limit the daily-hours warning dialog falls back to (useNurseWarnings).
			longShiftDurationHours: chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING,
			predictions,
			nurseOccurrences,
			showActual,
			getShiftTimes,
			t,
			lastNameFirst,
		};
		return candidates.map((nurse) => buildCandidateRow(context, nurse));
	}, [
		window,
		candidates,
		chosenDepartment,
		hospitalData,
		weekIndex,
		dayNo,
		shiftNo,
		staffingDate,
		dateRange,
		weekScheduleIndex,
		predictions,
		nurseOccurrences,
		showActual,
		getShiftTimes,
		t,
		lastNameFirst,
	]);

	const availableCount = useMemo(() => rows.filter((row) => row.unavailableReason == null).length, [rows]);

	return { rows, dateRange, availableCount };
};
